import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import {
  CoachEventDetail,
  dispatchCoachEvent,
  MILESTONE_FOR_EVENT,
  MilestoneName,
  onCoachEvent,
  PlainEventName,
  trackCoach,
} from '../../lib/coachEvents';
import { CoachUserLike, CoachWriteDeps, recordMilestone, syncCoachState } from '../../lib/coachJourney';

/**
 * CoachMilestoneBridge — turns plain coach events into once-per-account
 * milestones, records them, and re-dispatches them for the nudges.
 *
 * Renders nothing. Mounted inside CoachMarkProvider, next to CoachMarkHost
 * (App.tsx), so the Provider's nudge matcher is already listening when a
 * milestone is re-dispatched.
 *
 *   app-requested  → first-app       (a build request was sent from home)
 *   build-finished → first-build     (THE aha: their own app in the preview)
 *                    → journey-done  (derived, right after first-build)
 *   publish-live   → first-publish
 *
 * The plain events fire every time; `recordMilestone` is the once gate (it
 * returns false when the account already has the milestone, and it writes
 * the local cache synchronously so a second tab dedupes too). Milestones are
 * recorded for EVERY account — that is the activation data — while whether a
 * card is shown for them is decided elsewhere (lib/coachJourney's
 * `nudgeDecision`, which gates on the new-user journey).
 *
 * Also: on sign-in it promotes v1 localStorage tour state to the account and
 * flushes anything a previous session failed to save; and it dispatches
 * `route-enter` on every navigation so Markdown nudges can say
 * `when: route-enter`.
 */
export const CoachMilestoneBridge: React.FC = () => {
  const { user, mergeUser } = useAuth();
  const location = useLocation();

  const userRef = useRef<CoachUserLike | null>(null);
  userRef.current = (user as unknown as CoachUserLike) || null;
  const userId = user?.id || null;
  // The pending "wait for the tab to become visible" listener from `handle`
  // below, so the owning effect's cleanup can remove it if the component
  // unmounts before the tab ever becomes visible.
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  const deps = useMemo<CoachWriteDeps>(
    () => ({
      setTourState: authAPI.setTourState,
      recordJourneyMilestone: authAPI.recordJourneyMilestone,
      mergeUser: mergeUser as CoachWriteDeps['mergeUser'],
    }),
    [mergeUser],
  );

  // Promotion + retry, once per session per account (syncCoachState dedupes).
  useEffect(() => {
    const u = userRef.current;
    if (!userId || !u) return;
    void Promise.resolve(syncCoachState(u, deps)).catch(() => {});
  }, [userId, deps]);

  useEffect(() => {
    dispatchCoachEvent('route-enter', { pathname: location.pathname });
  }, [location.pathname]);

  useEffect(
    () => {
      const unsubscribe = onCoachEvent((detail) => {
        void handle(detail);
      });
      return () => {
        unsubscribe();
        if (visibilityHandlerRef.current) {
          document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
          visibilityHandlerRef.current = null;
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deps],
  );

  async function handle(detail: CoachEventDetail): Promise<void> {
    if (detail.milestone) return; // our own re-dispatch
    const milestone = MILESTONE_FOR_EVENT[detail.name as PlainEventName];
    const u = userRef.current;
    if (!milestone || !u) return;

    let recorded = false;
    try {
      recorded = await recordMilestone(u, milestone, deps);
    } catch {
      recorded = false;
    }
    if (!recorded) return;

    trackCoach('coach_milestone', { name: milestone, task_id: detail.taskId });
    const { name: _ignored, ...rest } = detail;
    const fire = (m: MilestoneName) => dispatchCoachEvent(m, { ...rest, milestone: true });

    if (milestone === 'first-build') {
      // The aha has to be SEEN. If the tab is hidden when the build lands,
      // hold the card until the person comes back; the record above is not
      // held — the milestone happened.
      if (typeof document !== 'undefined' && document.hidden) {
        const once = () => {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', once);
          visibilityHandlerRef.current = null;
          fire('first-build');
        };
        visibilityHandlerRef.current = once;
        document.addEventListener('visibilitychange', once);
      } else {
        fire('first-build');
      }
      let done = false;
      try {
        done = await recordMilestone(userRef.current || u, 'journey-done', deps);
      } catch {
        done = false;
      }
      if (done) {
        trackCoach('coach_milestone', { name: 'journey-done' });
        dispatchCoachEvent('journey-done', { milestone: true });
      }
      return;
    }

    fire(milestone);
  }

  return null;
};

export default CoachMilestoneBridge;
